import { integrationFieldDatesAreSameDay } from '@aline/common';
import { Apollo } from 'src/gql/generated';

/**
 * UI status for an integration field in the right-panel / variable alert.
 * Primary happy paths are `addedToTemplate` and `synced`; the rest flag bugs or actionable mismatch.
 */
export type IntegrationFieldStatus =
  /**
   * Template document: the field is bound to the CRM path. No CRM value is expected yet.
   * Footer / alert: "Added to template".
   */
  | 'addedToTemplate'
  /**
   * Workflow document: doc text differs from the CRM value (format-normalized).
   * Footer / alert: "Value out of sync with …" plus a re-sync action.
   */
  | 'outOfSync'
  /**
   * Workflow document: CRM read succeeded (`valueStatus: AVAILABLE`), including when the CRM
   * value is empty. Empty is a normal Salesforce state, not a failure.
   * Footer / alert: "Value synced with …".
   */
  | 'synced'
  /**
   * The variable is in the doc but no matching field came back in the CRM payload
   * (identity mismatch, catalog miss, etc.).
   * Alert: "Unable to load value from …".
   */
  | 'unavailable'
  /**
   * The field is present in the CRM payload, but Aline never successfully read its value
   * (`valueStatus: UNAVAILABLE` — no SOQL plan, rejected query, invisible record, …).
   * Distinct from `unavailable` (missing field) and from `synced` with an empty value.
   * Footer / alert: "Failed to load value from …".
   */
  | 'valueUnavailable';

type GetIntegrationFieldStatusOptions = {
  hasIntegrationField: boolean;
  isStale: boolean;
  isTemplate: boolean;
  valueStatus: Apollo.IntegrationFieldValueStatus | null | undefined;
};

/**
 * Maps CRM payload presence + `valueStatus` + template/stale flags onto a UI status.
 * Empty CRM values with `AVAILABLE` are `synced` / `addedToTemplate` — only a failed read
 * surfaces as `valueUnavailable`.
 */
export function getIntegrationFieldStatus({
  hasIntegrationField,
  isStale,
  isTemplate,
  valueStatus,
}: GetIntegrationFieldStatusOptions): IntegrationFieldStatus {
  if (!hasIntegrationField) return 'unavailable';
  if (valueStatus === Apollo.IntegrationFieldValueStatus.Unavailable) return 'valueUnavailable';
  if (isTemplate) return 'addedToTemplate';
  return isStale ? 'outOfSync' : 'synced';
}

/**
 * Whether the text stored in the document matches the value from the CRM opportunity.
 * Used for "stale" UI — this is separate from pushing the Aline Document record to Salesforce.
 *
 * Native Salesforce SOQL values often differ in *format* from Apideck unified values for the
 * same field (ISO `2026-07-02` vs unified `Jul 2, 2026`, trailing decimal zeros, CRLF).
 * Treat those as equal so dual-catalog native rows are not falsely flagged out of sync.
 *
 * Empty CRM + document text that is only the field title/label (template placeholder) counts
 * as in sync — e.g. unset Account.Ownership left as "Ownership" in the template.
 */
export function integrationFieldValueMatchesDocument(
  documentValue: string | null | undefined,
  opportunityValue: string | null | undefined,
  placeholderTexts?: ReadonlyArray<string | null | undefined>
) {
  const doc = (documentValue ?? '').trim();
  const opp = (opportunityValue ?? '').trim();

  if (!opp) {
    if (!doc) return true;
    const placeholders = new Set(
      (placeholderTexts ?? []).map(text => (text ?? '').trim()).filter(Boolean)
    );
    if (placeholders.has(doc)) return true;
  }

  if (doc === opp) return true;
  if (normalizeNewlines(doc) === normalizeNewlines(opp)) return true;

  const docNum = Number(doc);
  const oppNum = Number(opp);
  if (
    doc !== '' &&
    opp !== '' &&
    Number.isFinite(docNum) &&
    Number.isFinite(oppNum) &&
    !Number.isNaN(docNum) &&
    !Number.isNaN(oppNum)
  ) {
    return docNum === oppNum;
  }

  return integrationFieldDatesAreSameDay(doc, opp);
}

// Salesforce / Word pastes often use CRLF; normalize before string compare.
function normalizeNewlines(value: string) {
  return value.replaceAll('\r\n', '\n');
}
